import { MultiSelect, type MultiSelectProps } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { useStudentOids } from '@features/send-teams-message/query'
import { useResolveStudents } from '@features/communications/queries'
import { useStudentLists } from '../queries'

const communicationGroupsCategoryId = 1
const listValuePrefix = 'list:'

type Props = Omit<MultiSelectProps, 'data' | 'value' | 'defaultValue' | 'onChange'> & {
  value: string[]
  onChange: (value: string[]) => void
  onResolvedChange?: (studentValues: string[]) => void
  studentValuePrefix?: string
}

export function StudentRecipientSelect({ value, onChange, onResolvedChange, studentValuePrefix = '', disabled, ...props }: Props) {
  const listsQuery = useStudentLists()
  const studentsQuery = useStudentOids()
  const [search, setSearch] = useState('')
  const lists = useMemo(() => listsQuery.data ?? [], [listsQuery.data])
  const presets = lists.filter(list => list.categories.some(category => category.id === communicationGroupsCategoryId))
  const yourLists = lists.filter(list => !list.categories.some(category => category.id === communicationGroupsCategoryId))
  const formatStudent = (studentNumber: string) => `${studentValuePrefix}${studentNumber}`
  const resolutionQuery = useResolveStudents({ selectors: value }, Boolean(onResolvedChange))
  const presetItems = presets.map(list => ({ value: `${listValuePrefix}${list.id}`, label: list.name }))
  const individualItems = (studentsQuery.data ?? [])
      .map(student => ({ value: formatStudent(String(student.studentNo)), label: `${student.preferredName || student.firstName} ${student.surname} (${student.studentNo})` }))
      .filter(option => search.trim().length > 0 || value.includes(option.value))
  const yourListItems = yourLists.length > 0
    ? yourLists.map(list => ({ value: `${listValuePrefix}${list.id}`, label: list.name }))
    : [{ value: '$no-saved-lists', label: 'You have no saved lists', disabled: true }]
  const data = [
    ...(presetItems.length > 0 ? [{ group: 'Preset groups', items: presetItems }] : []),
    ...(individualItems.length > 0 ? [{ group: 'Individual students', items: individualItems }] : []),
    { group: 'Your lists', items: yourListItems },
  ]

  useEffect(() => {
    if (!onResolvedChange) return
    onResolvedChange((resolutionQuery.data?.students ?? []).map(student => formatStudent(String(student.studentNumber))))
  }, [onResolvedChange, resolutionQuery.data?.students, studentValuePrefix])

  const handleChange = (next: string[]) => {
    if (next.includes('$no-saved-lists')) return
    onChange(next)
  }

  return <MultiSelect
    {...props}
    data={data}
    value={value}
    onChange={values => void handleChange(values)}
    disabled={disabled || listsQuery.isLoading || studentsQuery.isLoading}
    searchable
    searchValue={search}
    onSearchChange={setSearch}
    hidePickedOptions
    nothingFoundMessage="No matching students or lists"
  />
}
